import { useNavigate } from "react-router-dom";

import { NotificationTopicOwnersSettingsSection } from "@/components/NotificationTopicOwnersSettingsSection";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { CardTitleWithHelp, PageTitleWithHelp } from "@/components/ui/title-help";
import { apiFetch } from "@/lib/api";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";

const NOTIFICATION_STAFF_USERS_QUERY_KEY = ["notifications", "staff-user-options"] as const;

function fetchNotificationStaffUsers(): Promise<{
  users: Array<{ userId: string; email: string; displayName: string | null }>;
}> {
  return apiFetch("/api/notifications/staff-user-options");
}

export function QualitySettingsPage() {
  const canEdit = useHubModuleAdmin("quality");
  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      <header>
        <PageTitleWithHelp hoverHint="Quality module settings: notification distribution lists for RMA receiving discrepancies and other quality workflows.">
          Quality · Settings
        </PageTitleWithHelp>
      </header>

      <NotificationTopicOwnersSettingsSection
        topic="quality_rma_receive_quantity_discrepancy"
        title="RMA receive quantity discrepancy"
        hoverHint="Hub users in this group receive email and in-app notifications when receiving finishes an RMA with a device count that does not match the ordered quantity on the RMA."
        canEdit={canEdit}
        userOptionsQueryKey={NOTIFICATION_STAFF_USERS_QUERY_KEY}
        fetchUserOptions={fetchNotificationStaffUsers}
        readOnlyHoverHint="Quality module administrators can edit this distribution list."
      />
      <Card>
        <CardHeader>
          <CardTitleWithHelp hoverHint="The catalog of observable failure modes. Each mode can be linked to tier 1 scripts. A mode that is already on an NCR cannot be deleted.">
            Manage failure modes
          </CardTitleWithHelp>
        </CardHeader>
        <CardBody>
          <Button type="button" variant="secondary" onClick={() => navigate("/quality/settings/failure-modes")}>
            Manage failure modes
          </Button>
        </CardBody>
      </Card>
      <Card>
        <CardHeader>
          <CardTitleWithHelp hoverHint="Prompts shown to the operator for immediate troubleshooting. Link them to failure modes from Manage failure modes.">
            Manage operator scripts
          </CardTitleWithHelp>
        </CardHeader>
        <CardBody>
          <Button type="button" variant="secondary" onClick={() => navigate("/quality/settings/operator-scripts")}>
            Manage operator scripts
          </Button>
        </CardBody>
      </Card>
      <Card>
        <CardHeader>
          <CardTitleWithHelp hoverHint="One prompt per line. A script is used when an NCR has that tier 1 issue.">
            Manage tier 1 scripts
          </CardTitleWithHelp>
        </CardHeader>
        <CardBody>
          <Button type="button" variant="secondary" onClick={() => navigate("/quality/settings/tier-1-scripts")}>
            Manage tier 1 scripts
          </Button>
        </CardBody>
      </Card>
    </div>
  );
}
